/**
 * /fit · the Wellness & Fitness pillar surface · Google Places-backed.
 *
 * Mirror of /stay (Stay.jsx) in shape and discipline · differs only in
 * vocabulary (venue/category instead of hotel/dates) and the category
 * chip selector that lives where /stay's date pickers live.
 *
 * Surfaces:
 *   · Hero with category chips + city search
 *   · Top wellness cities rail (Bali · Ubud · Rishikesh · Tulum…)
 *   · Card grid with smart filters (rating · price · type · sort)
 *   · Rich detail drawer (gallery · reviews · hours · landmarks)
 *   · "Continue on Google ↗" + "Request via Concierge®" CTAs
 *
 * Embed mode: `?embed=widget` hides global chrome via embed.css ·
 * brief modal posts to /api/fit/request which goes through the
 * Aurelion dispatcher (tenant=aurelion|mindescapes → MindEscapes Zoho).
 */
import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useEmbedMember } from "@/hooks/useEmbedMember";
import { TableFooter } from "@/components/table/TableFooter";
import { PlacesAutocomplete } from "@/components/shared/PlacesAutocomplete";
import ConciergeHero from "@/components/global/ConciergeHero";
import ConciergeOnDutyPill from "@/components/global/ConciergeOnDutyPill";
import ConciergeSurfaceMenu from "@/components/global/ConciergeSurfaceMenu";
import CrossSurfaceNav from "@/components/global/CrossSurfaceNav";
import TopCitiesGrid, { PICKS_FIT } from "@/components/global/TopCitiesGrid";
import StaticMapBand from "@/components/shared/StaticMapBand";
import { DrawerVoiceLine, DrawerReviewsBand } from "@/components/shared/DrawerAtmosphere";
import ReservePill from "@/components/shared/ReservePill";
import FitTopBar from "@/components/fit/FitTopBar";
import PillarCard from "@/components/shared/PillarCard";

import MemberContextBand from "../components/MemberContextBand";
const API = process.env.REACT_APP_BACKEND_URL || "";

// Editorial tokens (synced with /stay · /escape · /enjoy)
const T = {
  bg:     "#F4EFE6",
  card:   "#FCFAF4",
  ink:    "#211C15",
  ink2:   "#5C5447",
  ink3:   "#8A8071",
  line:   "#E5DCCB",
  line2:  "#D8CDB7",
  brass:  "#8B6914",
};

const CATEGORIES = [
  { code: "all",     label: "All",                  short: "All" },
  { code: "spa",     label: "Spa & Wellness",       short: "Spa" },
  { code: "yoga",    label: "Yoga & Pilates",       short: "Yoga" },
  { code: "gym",     label: "Gym & Training",       short: "Gym" },
  { code: "massage", label: "Massage & Recovery",   short: "Recovery" },
  { code: "pool",    label: "Pools & Sports",       short: "Sports" },
];

export default function FitPage() {
  const navigate = useNavigate();
  const [q, setQ]               = useState("");
  const [category, setCategory] = useState("all");
  const [results, setResults]   = useState(null);
  const [loading, setLoading]   = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError]       = useState(null);
  const [briefVenue, setBriefVenue] = useState(null);  // null | venueCard | {open_ask:true}
  const [openDetail, setOpenDetail] = useState(null);  // drawer · null | venueCard
  // Smart filters · same pattern as /stay · client-side on the loaded set.
  const [filterPrice, setFilterPrice] = useState("any");     // any · "$" · "$$" · "$$$" · "$$$$"
  const [filterMinRating, setFilterMinRating] = useState(0); // 0 · 3 · 4 · 4.5
  const [sortBy, setSortBy]           = useState("rating");  // rating · popularity · price_asc · price_desc · name

  // Viewport-aware search-bar layout · same fix shipped to /stay.
  const [isNarrow, setIsNarrow] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(max-width: 760px)");
    const update = () => setIsNarrow(mq.matches);
    update();
    if (mq.addEventListener) mq.addEventListener("change", update);
    else mq.addListener(update);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", update);
      else mq.removeListener(update);
    };
  }, []);

  async function runSearch(query, cat) {
    const term = (query ?? q).trim();
    const useCat = cat ?? category;
    if (!term) return;
    setLoading(true);
    setError(null);
    // Clear previous results · do not show old city through the gap.
    setResults(null);
    setFilterPrice("any");
    setFilterMinRating(0);
    setSortBy("rating");
    try {
      const url = new URL(`${API}/api/fit/search`);
      url.searchParams.set("q", term);
      url.searchParams.set("category", useCat);
      const r = await fetch(url.toString());
      const data = await r.json();
      if (!r.ok) throw new Error(data?.detail || `search_failed (${r.status})`);
      setResults(data);
    } catch (e) {
      setError(e.message || "Could not load venues.");
    } finally {
      setLoading(false);
    }
  }

  async function loadMore() {
    if (!results?.next_page_token || loadingMore) return;
    setLoadingMore(true);
    try {
      const url = new URL(`${API}/api/fit/search`);
      url.searchParams.set("q", results.city_query);
      url.searchParams.set("category", results.category || "all");
      url.searchParams.set("page_token", results.next_page_token);
      const r = await fetch(url.toString());
      const data = await r.json();
      if (!r.ok) throw new Error(data?.detail || `loadmore_failed (${r.status})`);
      setResults((prev) => ({
        ...prev,
        cards: [...(prev.cards || []), ...(data.cards || [])],
        next_page_token: data.next_page_token || null,
      }));
    } catch (e) {
      setError(e.message || "Could not load more venues.");
    } finally {
      setLoadingMore(false);
    }
  }

  // Switching category while we already have a city · re-run search.
  function pickCategory(code) {
    setCategory(code);
    if (q.trim()) {
      runSearch(q, code);
    }
  }

  return (
    <div data-testid="fit-page-root" data-table-surface="fit" style={{ background: T.bg, minHeight: "100vh", color: T.ink, fontFamily: "'Hanken Grotesk', system-ui, sans-serif", "--t-bg": T.bg, "--t-ink": T.ink, "--t-ink2": T.ink2, "--t-ink3": T.ink3, "--t-line": T.line, "--t-brass": T.brass, "--t-verified": "#2f8a52" }}>
      <FitTopBar onHome={() => { setResults(null); setQ(""); }} />
      <MemberContextBand pillar="fit" />

      <main style={{ maxWidth: 1180, margin: "0 auto", padding: "12px 28px 80px" }}>
{/* Hero · mirrors /stay */}
        <ConciergeHero
          testid="fit-hero"
          eyebrow="Concierge® · Fit"
          headline="Where will you take"
          headlineTail="care of yourself?"
          subtitle={<>Spas, gyms, yoga, recovery&mdash;with the Concierge® beside you.</>}
          maxWidth={980}
        >
          {/* Category chip row (always visible) */}
          <div
            data-testid="fit-category-row"
            style={{
              display: "flex",
              justifyContent: "center",
              flexWrap: "wrap",
              gap: 8,
              marginBottom: 16,
            }}
          >
            {CATEGORIES.map((c) => {
              const on = category === c.code;
              return (
                <button
                  key={c.code}
                  type="button"
                  data-testid={`fit-category-${c.code}`}
                  onClick={() => pickCategory(c.code)}
                  style={{
                    background: on ? T.ink : "transparent",
                    color: on ? T.bg : T.ink2,
                    border: `1px solid ${on ? T.ink : T.line2}`,
                    borderRadius: 999,
                    padding: isNarrow ? "7px 12px" : "8px 16px",
                    fontSize: 12.5, fontWeight: 500,
                    cursor: "pointer", fontFamily: "inherit",
                    transition: "all 0.12s",
                  }}
                >
                  {isNarrow ? c.short : c.label}
                </button>
              );
            })}
            {/* Walks · sibling pillar surface, lives under /fit/walks.
                Founder ruling 1 Jul 2026 — walks is a tab on /fit. */}
            <button
              type="button"
              data-testid="fit-category-walks"
              onClick={() => navigate("/fit/walks")}
              style={{
                background: "transparent",
                color: T.brass,
                border: `1px solid ${T.brass}`,
                borderRadius: 999,
                padding: isNarrow ? "7px 12px" : "8px 16px",
                fontSize: 12.5, fontWeight: 500,
                cursor: "pointer", fontFamily: "inherit",
                transition: "all 0.12s",
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = T.brass; e.currentTarget.style.color = T.bg; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = T.brass; }}
            >
              {isNarrow ? "Walks" : "Walks ↗"}
            </button>
          </div>

          {/* Search bar · city + go */}
          <form
            data-testid="fit-search-bar"
            onSubmit={(e) => { e.preventDefault(); runSearch(); }}
            style={{
              background: T.card,
              border: `1px solid ${T.line2}`,
              borderRadius: 18,
              padding: isNarrow ? "12px" : "8px 8px 8px 18px",
              display: "grid",
              gridTemplateColumns: isNarrow ? "1fr" : "1fr auto",
              gap: isNarrow ? 10 : 8,
              alignItems: "center",
              maxWidth: 760,
              margin: "0 auto",
              boxShadow: "0 6px 22px rgba(33,28,21,0.04)",
            }}
          >
            <div style={{ position: "relative", flex: 1, minWidth: 0 }}>
              <PlacesAutocomplete
                value={q}
                onChange={setQ}
                onSubmit={(picked) => { setQ(picked); runSearch(picked); }}
                placeholder="City — Bali · Ubud · Mumbai · London…"
                types="(cities)"
                testidPrefix="fit-search"
                inputStyle={{
                  border: 0, background: "transparent", outline: "none",
                  borderRadius: 0, fontSize: 16, color: T.ink,
                  padding: "10px 12px",
                  fontFamily: "inherit",
                }}
              />
            </div>
            <button
              type="submit"
              data-testid="fit-search-submit"
              disabled={loading || !q.trim()}
              style={{
                background: T.ink, color: T.card, border: 0, borderRadius: 14,
                padding: "12px 22px", fontSize: 14, fontWeight: 600,
                cursor: loading ? "wait" : (!q.trim() ? "not-allowed" : "pointer"),
                opacity: (loading || !q.trim()) ? 0.65 : 1,
                fontFamily: "inherit",
                width: isNarrow ? "100%" : "auto",
              }}
            >
              {loading ? "Searching…" : "Find venues"}
            </button>
          </form>

          {/* Two CTA row · matches /stay */}
          <div
            data-testid="fit-hero-secondary-row"
            style={{
              display: "flex", justifyContent: "center", alignItems: "center",
              gap: 18, marginTop: 18, flexWrap: "wrap",
            }}
          >
            <button
              type="button"
              data-testid="fit-browse"
              onClick={() => runSearch("Bali")}
              style={{
                background: "transparent",
                color: T.ink,
                border: `1.5px solid ${T.ink}`,
                borderRadius: 999,
                padding: "9px 18px",
                fontSize: 13,
                fontWeight: 500,
                cursor: "pointer",
                fontFamily: "inherit",
              }}
            >
              Browse wellness
            </button>
            <button
              type="button"
              data-testid="fit-hero-ask-concierge"
              onClick={() => setBriefVenue({ _open_ask: true })}
              style={{
                background: "transparent",
                color: T.brass,
                border: `1px solid ${T.brass}`,
                borderRadius: 999,
                padding: "10px 22px",
                fontSize: 13.5,
                fontWeight: 500,
                cursor: "pointer",
                fontFamily: "inherit",
                transition: "all 0.15s",
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = T.brass; e.currentTarget.style.color = T.bg; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = T.brass; }}
            >
              Ask the Concierge®
            </button>
          </div>
          <p
            style={{
              fontSize: 12.5,
              color: T.ink3,
              fontStyle: "italic",
              marginTop: 14,
              marginBottom: 0,
            }}
          >
            Visit directly or ask your Concierge® to arrange it.
          </p>
        </ConciergeHero>

        {/* Top destinations · landing state only */}
        {!results && !loading && !error && (
          <TopCitiesGrid
            pillar="fit"
            picks={PICKS_FIT}
            onPick={(query) => { setQ(query); runSearch(query); }}
            eyebrow="Wellness cities"
            title="Where wellness lives"
          />
        )}

        {/* Results */}
        {error && (
          <p data-testid="fit-error" style={{ textAlign: "center", color: "#9a3535", fontSize: 14 }}>{error}</p>
        )}
        {results && results.cards?.length === 0 && (
          <div data-testid="fit-empty" style={{ textAlign: "center", padding: "48px 20px", color: T.ink2, maxWidth: 620, margin: "0 auto" }}>
            <p style={{ fontSize: 15, marginBottom: 18 }}>
              No live venues found for <strong style={{ color: T.ink }}>{results.city_query}</strong>
              {results.category_label ? <> in <em>{results.category_label}</em></> : null}.
              The Concierge® will find it by hand.
            </p>
            <button
              type="button"
              onClick={() => setBriefVenue({ _open_ask: true, hint_city: results.city_query })}
              style={{ background: T.ink, color: T.card, border: 0, borderRadius: 999, padding: "12px 24px", fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}
            >
              Send a brief
            </button>
          </div>
        )}
        {results && results.cards?.length > 0 && (
          <>
            <FitFilterBar
              cards={results.cards}
              filterPrice={filterPrice}      setFilterPrice={setFilterPrice}
              filterMinRating={filterMinRating} setFilterMinRating={setFilterMinRating}
              sortBy={sortBy}                setSortBy={setSortBy}
            />
            <FilteredResultsGrid
              cards={results.cards}
              filterPrice={filterPrice}
              filterMinRating={filterMinRating}
              sortBy={sortBy}
              onOpen={setOpenDetail}
              onBrief={(v) => setBriefVenue(v)}
            />
            {results.next_page_token && (
              <div style={{ display: "flex", justifyContent: "center", marginTop: 36 }}>
                <button
                  type="button"
                  data-testid="fit-load-more"
                  onClick={loadMore}
                  disabled={loadingMore}
                  style={{
                    background: "transparent", color: T.ink,
                    border: `1.5px solid ${T.ink}`, borderRadius: 999,
                    padding: "12px 28px", fontSize: 13.5, fontWeight: 500,
                    cursor: loadingMore ? "wait" : "pointer",
                    fontFamily: "inherit",
                    opacity: loadingMore ? 0.6 : 1,
                  }}
                >
                  {loadingMore ? "Loading…" : `Load more · ${results.cards.length} shown`}
                </button>
              </div>
            )}
          </>
        )}
      </main>

      <TableFooter slug="fit" />

      {briefVenue && (
        <FitRequestModal
          venue={briefVenue}
          city={results?.city_query || briefVenue.hint_city || ""}
          category={results?.category || category}
          onClose={() => setBriefVenue(null)}
        />
      )}

      {openDetail && (
        <FitDetailDrawer
          card={openDetail}
          onClose={() => setOpenDetail(null)}
          onRequest={() => { setBriefVenue(openDetail); setOpenDetail(null); }}
        />
      )}
    </div>
  );
}

// ── Venue card · clickable · the atmosphere lives in the drawer ──
function VenueCard({ venue, onOpen }) {
  const hasPhoto = !!venue.photo_url;
  const rating = venue.rating ? Number(venue.rating) : null;
  const ratingCount = venue.user_ratings_total;
  const priceTier = _priceTierLabel(venue.price_level);
  const reserveHref = venue.google_maps_url || venue.maps_url || venue.website || null;
  return (
    <article
      data-testid="fit-card"
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpen?.(); } }}
      style={{
        background: T.card, border: `1px solid ${T.line}`, borderRadius: 16,
        display: "flex", flexDirection: "column",
        overflow: "hidden",
        cursor: "pointer",
        transition: "transform 0.15s ease, box-shadow 0.15s ease",
      }}
      onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 12px 32px rgba(33,28,21,0.10)"; }}
      onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0)";  e.currentTarget.style.boxShadow = "none"; }}
    >
      <div
        data-testid="fit-card-photo"
        style={{
          aspectRatio: "4 / 3",
          position: "relative",
          background: hasPhoto
            ? `linear-gradient(180deg, rgba(33,28,21,0) 55%, rgba(33,28,21,0.18) 100%), url(${venue.photo_url}) center/cover no-repeat`
            : `linear-gradient(135deg, ${T.line} 0%, ${T.line2} 60%, ${T.brass}33 100%)`,
          position: "relative",
        }}
      >
        {!hasPhoto && (
          <div style={{
            position: "absolute", inset: 0, display: "flex",
            alignItems: "center", justifyContent: "center",
            fontFamily: "'Fraunces', 'EB Garamond', serif",
            fontSize: 30, fontWeight: 500, color: T.brass,
            letterSpacing: "-0.01em",
          }}>
            {(venue.name || "F").trim().charAt(0).toUpperCase()}
          </div>
        )}
        {priceTier && (
          <span
            data-testid="fit-card-price-tier"
            style={{
              position: "absolute", top: 12, right: 12,
              background: "rgba(33,28,21,0.78)", color: T.bg,
              padding: "3px 9px", borderRadius: 999,
              fontFamily: "ui-monospace, monospace",
              fontSize: 10, letterSpacing: "0.16em",
              fontWeight: 600,
            }}
          >
            {priceTier}
          </span>
        )}
        {venue.primary_type_label && (
          <span
            data-testid="fit-card-type-pill"
            style={{
              position: "absolute", top: 12, left: 12,
              background: "rgba(252,250,244,0.92)", color: T.brass,
              padding: "3px 9px", borderRadius: 999,
              fontFamily: "ui-monospace, monospace",
              fontSize: 10, letterSpacing: "0.14em",
              fontWeight: 600, textTransform: "uppercase",
              border: `1px solid ${T.brass}44`,
            }}
          >
            {venue.primary_type_label}
          </span>
        )}
        {/* Reserve pill · front-of-card primary action across all
           pillars (founder ruling 30 Jun 2026 evening). Site + phone
           live inside the drawer; the card carries the quick reserve. */}
        <ReservePill href={reserveHref} testid="fit-card-reserve" T={T} intentMeta={{ benefit: "fit", venue: v }} />
      </div>
      <div style={{ padding: 16, display: "flex", flexDirection: "column", gap: 6, flex: 1 }}>
        <h3 className="t-serif" style={{ fontSize: 17, fontWeight: 500, lineHeight: 1.22, margin: 0, color: T.ink }}>
          {venue.name || "Untitled venue"}
        </h3>
        <p style={{ fontSize: 11.5, color: T.ink3, margin: 0, lineHeight: 1.4 }}>
          {venue.short_address || venue.address_line || venue.city_name || "—"}
        </p>
        {rating && (
          <p
            data-testid="fit-card-rating"
            style={{ fontSize: 12, color: T.ink2, margin: 0, display: "flex", alignItems: "center", gap: 5, marginTop: 2 }}
          >
            <span style={{ color: T.brass, fontWeight: 700 }}>★</span>
            <span style={{ fontWeight: 600, color: T.ink }}>{rating.toFixed(1)}</span>
            {ratingCount ? (
              <span style={{ color: T.ink3 }}>
                ({ratingCount.toLocaleString()})
              </span>
            ) : null}
          </p>
        )}
      </div>
    </article>
  );
}

function _priceTierLabel(level) {
  if (!level) return null;
  const map = {
    PRICE_LEVEL_INEXPENSIVE:    "$",
    PRICE_LEVEL_MODERATE:       "$$",
    PRICE_LEVEL_EXPENSIVE:      "$$$",
    PRICE_LEVEL_VERY_EXPENSIVE: "$$$$",
  };
  return map[level] || null;
}


// ── Brief modal · mirrors StayRequestModal ──────────────────────
function FitRequestModal({ venue, city, category, onClose }) {
  const isOpenAsk = !!venue?._open_ask;
  const initialVenueName = isOpenAsk ? "" : (venue.name || "");
  const initialCity = isOpenAsk ? (venue.hint_city || city || "") : (venue.city_name || city || "");

  const embedMember = useEmbedMember();
  const [venueName, setVenueName] = useState(initialVenueName);
  const [cityVal, setCityVal]     = useState(initialCity);
  const [name, setName]           = useState(embedMember?.name  || "");
  const [email, setEmail]         = useState(embedMember?.email || "");
  const [phone, setPhone]         = useState(embedMember?.phone || "");
  const [partyAdults, setPartyAdults] = useState(1);
  const [date, setDate]           = useState("");
  const [time, setTime]           = useState("");
  const [duration, setDuration]   = useState("");
  const [occasion, setOccasion]   = useState("");
  const [preferences, setPreferences] = useState("");
  const [busy, setBusy]           = useState(false);
  const [error, setError]         = useState(null);
  const [done, setDone]           = useState(null);

  async function submit(e) {
    e.preventDefault();
    setError(null);
    if (isOpenAsk && !venueName.trim() && !cityVal.trim()) {
      setError("Tell us a city — or the venue you have in mind.");
      return;
    }
    if (!name.trim() || (!email.trim() && !phone.trim())) {
      setError("Your name plus email or phone — so the Concierge® can reply.");
      return;
    }
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/fit/request`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          venue_name: venueName.trim() || venue.name || null,
          venue_id:   venue.place_id || null,
          city:       cityVal.trim() || null,
          country:    venue.country_code || null,
          category:   category || venue.category || null,
          date:       date || null,
          time:       time.trim() || null,
          duration:   duration.trim() || null,
          adults:     Number(partyAdults) || 1,
          preferences: preferences.trim() || null,
          occasion:    occasion.trim() || null,
          member_name:  name.trim(),
          member_email: email.trim() || undefined,
          member_phone: phone.trim() || undefined,
        }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data?.detail || `submit_failed (${r.status})`);
      setDone(data);
    } catch (e) {
      setError(e.message || "Could not send. Please try again.");
    } finally {
      setBusy(false);
    }
  }

  if (done) {
    return (
      <Backdrop onClose={onClose}>
        <div data-testid="fit-request-success" style={modalStyle}>
          <div style={{ fontFamily: "ui-monospace, monospace", fontSize: 11, letterSpacing: "0.22em", color: T.brass, fontWeight: 700 }}>BY THE CONCIERGE® · FIT</div>
          <h2 className="t-serif" style={{ fontSize: 26, fontWeight: 500, margin: "10px 0 14px", color: T.ink }}>Your brief is with the Concierge®.</h2>
          <p style={{ fontSize: 14.5, color: T.ink2, lineHeight: 1.55 }}>
            We&apos;ll be in touch on <strong style={{ color: T.ink }}>{email || phone}</strong> very shortly with the next step.
          </p>
          <div data-testid="fit-request-ref" style={{ display: "inline-block", padding: "9px 16px", background: T.card, border: `1px solid ${T.brass}`, borderRadius: 999, fontFamily: "ui-monospace, monospace", fontSize: 12, color: T.brass, fontWeight: 600, marginTop: 16 }}>
            {done.display_ref || done.member_reference || "REF-PENDING"}
          </div>
          <div style={{ marginTop: 22 }}>
            <button type="button" onClick={onClose} style={{ background: T.ink, color: T.card, border: 0, borderRadius: 999, padding: "10px 22px", fontSize: 13.5, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>Close</button>
          </div>
        </div>
      </Backdrop>
    );
  }

  return (
    <Backdrop onClose={onClose}>
      <div data-testid="fit-request-modal" style={modalStyle} onClick={(e) => e.stopPropagation()}>
        <div style={{ fontFamily: "ui-monospace, monospace", fontSize: 11, letterSpacing: "0.22em", color: T.brass, fontWeight: 700 }}>BY THE CONCIERGE® · FIT</div>
        <h2 className="t-serif" style={{ fontSize: 24, fontWeight: 500, margin: "10px 0 8px", color: T.ink }}>
          {isOpenAsk ? "Your wellness of choice" : "Request via Concierge®"}
        </h2>
        <p style={{ fontSize: 13.5, color: T.ink2, margin: "0 0 18px" }}>
          {isOpenAsk
            ? "Tell us what and where — we'll match the right venue."
            : (venue.name || "")}
        </p>
        <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {isOpenAsk && (
            <>
              <Input value={venueName} onChange={setVenueName} placeholder="Venue of choice (optional)" testid="fit-modal-venue" />
              <Input value={cityVal} onChange={setCityVal} placeholder="City (helps a lot)" testid="fit-modal-city" autoFocus />
            </>
          )}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <Input value={date} onChange={setDate} placeholder="Date (YYYY-MM-DD)" testid="fit-modal-date" type="date" />
            <Input value={time} onChange={setTime} placeholder="Time (e.g. 10:30)" testid="fit-modal-time" />
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <Input value={duration} onChange={setDuration} placeholder="Duration (60 min · full day)" testid="fit-modal-duration" />
            <Input value={partyAdults} onChange={setPartyAdults} placeholder="People" testid="fit-modal-adults" type="number" />
          </div>
          <Input value={name} onChange={setName} placeholder="Your name" testid="fit-modal-name" />
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <Input value={email} onChange={setEmail} placeholder="Email" testid="fit-modal-email" type="email" />
            <Input value={phone} onChange={setPhone} placeholder="Phone / WhatsApp" testid="fit-modal-phone" />
          </div>
          <Input value={occasion} onChange={setOccasion} placeholder="Occasion (optional · birthday, retreat, post-flight…)" testid="fit-modal-occasion" />
          <textarea
            data-testid="fit-modal-preferences"
            value={preferences}
            onChange={(e) => setPreferences(e.target.value)}
            placeholder="Anything else? Preferred therapist, dietary needs, focus area…"
            rows={3}
            style={{ ...inputStyle, resize: "vertical", minHeight: 80 }}
          />
          {error && <p data-testid="fit-modal-error" style={{ color: "#9a3535", fontSize: 13, margin: 0 }}>{error}</p>}
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 6 }}>
            <button type="button" onClick={onClose} style={{ background: "transparent", color: T.ink2, border: `1px solid ${T.line2}`, borderRadius: 999, padding: "10px 18px", fontSize: 13.5, cursor: "pointer", fontFamily: "inherit" }}>Cancel</button>
            <button type="submit" data-testid="fit-modal-submit" disabled={busy} style={{ background: T.ink, color: T.card, border: 0, borderRadius: 999, padding: "10px 22px", fontSize: 13.5, fontWeight: 600, cursor: busy ? "wait" : "pointer", opacity: busy ? 0.65 : 1, fontFamily: "inherit" }}>
              {busy ? "Sending…" : "Send to Concierge® →"}
            </button>
          </div>
        </form>
      </div>
    </Backdrop>
  );
}

// ── Primitives ───────────────────────────────────────────────────
function Backdrop({ children, onClose }) {
  return (
    <div
      onClick={onClose}
      style={{ position: "fixed", inset: 0, background: "rgba(33,28,21,0.55)", zIndex: 80, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}
    >
      {children}
    </div>
  );
}

const modalStyle = {
  background: T.bg, color: T.ink, borderRadius: 18, padding: "28px 28px 22px",
  width: "min(540px, 100%)", maxHeight: "90vh", overflowY: "auto",
  boxShadow: "0 24px 60px rgba(0,0,0,0.25)",
};

const inputStyle = {
  width: "100%", padding: "11px 14px", border: `1px solid ${T.line2}`,
  borderRadius: 12, background: T.card, color: T.ink, fontSize: 14.5,
  fontFamily: "inherit", outline: "none", boxSizing: "border-box",
};

function Input({ value, onChange, placeholder, testid, type = "text", autoFocus }) {
  return (
    <input
      type={type}
      data-testid={testid}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      autoFocus={autoFocus}
      style={inputStyle}
    />
  );
}


/* ── FitTopBar · moved to components/fit/FitTopBar.jsx so Walks reuses it. */
function _FitTopBar_unused() { return null; }


/* ── FitDetailDrawer · clones StayDetailDrawer. Fetches /api/fit/place/{id}
 *    for rich data. Two CTAs:
 *      · "Continue on Google ↗" → google_maps_url
 *      · "Request via Concierge®" → opens brief modal */
function FitDetailDrawer({ card, onClose, onRequest }) {
  const [data, setData] = useState(card);
  const [loadingDetail, setLoadingDetail] = useState(true);
  const [activePhoto, setActivePhoto] = useState(0);
  const [voiceLine, setVoiceLine] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!card?.place_id) { setLoadingDetail(false); return; }
      try {
        const r = await fetch(`${API}/api/fit/place/${encodeURIComponent(card.place_id)}`);
        if (!r.ok) throw new Error(`detail_${r.status}`);
        const j = await r.json();
        if (cancelled) return;
        setData({ ...card, ...(j.venue || {}) });
      } catch {
        if (!cancelled) setData(card);
      } finally {
        if (!cancelled) setLoadingDetail(false);
      }
    })();
    return () => { cancelled = true; };
  }, [card?.place_id]);

  // Concierge® voice line · Parity Sprint
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const pid = data?.place_id || card?.place_id;
      if (!pid || !data?.name) return;
      try {
        const params = new URLSearchParams({ name: data.name });
        if (data.editorial_summary) params.set("editorial", String(data.editorial_summary).slice(0, 1500));
        if (data.neighborhood) params.set("neighborhood", data.neighborhood);
        if (data.city) params.set("city", data.city);
        if (data.category || data.primary_type_label) {
          params.set("types_label", data.primary_type_label || data.category);
        }
        const r = await fetch(`${API}/api/concierge/voice/fit/${encodeURIComponent(pid)}?${params.toString()}`);
        if (!r.ok) return;
        const j = await r.json();
        if (!cancelled && j?.voice_line) setVoiceLine(j.voice_line);
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [data?.place_id, card?.place_id, data?.name, data?.editorial_summary, data?.neighborhood, data?.city, data?.category, data?.primary_type_label]);

  const photos = (data.photos && data.photos.length ? data.photos : [data.photo_url].filter(Boolean));
  const hero = photos[activePhoto] || photos[0];
  const rating = data.rating ? Number(data.rating) : null;
  const ratingCount = data.user_ratings_total;
  const priceTier = _priceTierLabel(data.price_level);
  const openNow = data.open_now;

  function copyShareLink() {
    const url = `${API}/fit?q=${encodeURIComponent(data.name || "")}`;
    try {
      navigator.clipboard?.writeText(url);
    } catch { /* ignore */ }
  }

  return (
    <div
      data-testid="fit-drawer-overlay"
      onClick={onClose}
      style={{
        position: "fixed", inset: 0,
        background: "rgba(20,16,11,0.55)",
        display: "flex", justifyContent: "flex-end",
        zIndex: 1000,
      }}
    >
      <aside
        data-testid="fit-drawer"
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "min(560px, 96vw)",
          background: T.card,
          color: T.ink,
          height: "100%",
          overflowY: "auto",
          boxShadow: "-12px 0 36px rgba(0,0,0,0.22)",
          display: "flex", flexDirection: "column",
        }}
      >
        {/* Chrome · sticky */}
        <div
          data-testid="fit-drawer-chrome"
          style={{
            position: "sticky", top: 0, zIndex: 2,
            background: T.card,
            borderBottom: `1px solid ${T.line}`,
            padding: "14px 22px",
            display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10,
          }}
        >
          <span
            style={{
              fontFamily: "ui-monospace, monospace",
              fontSize: 10, letterSpacing: "0.22em",
              color: T.brass, fontWeight: 600, textTransform: "uppercase",
            }}
          >
            Wellness Routing
          </span>
          <div style={{ display: "flex", gap: 8 }}>
            <button
              type="button"
              data-testid="fit-drawer-share"
              onClick={copyShareLink}
              style={chromeBtnStyle}
            >Share ↗</button>
            <button
              type="button"
              data-testid="fit-drawer-print"
              onClick={() => window.print()}
              style={chromeBtnStyle}
            >↓ Save as card</button>
            <button
              type="button"
              data-testid="fit-drawer-close"
              onClick={onClose}
              aria-label="Close"
              style={{
                background: "transparent", border: 0,
                fontSize: 20, cursor: "pointer", color: T.ink2,
                padding: "0 8px",
              }}
            >×</button>
          </div>
        </div>

        {hero && (
          <div
            data-testid="fit-drawer-hero"
            style={{
              aspectRatio: "16 / 10",
              background: `url(${hero}) center/cover no-repeat`,
            }}
          />
        )}

        <div style={{ padding: "22px 26px 30px", display: "flex", flexDirection: "column", gap: 16 }}>
          {/* Branded ribbon */}
          <div
            data-testid="fit-drawer-ribbon"
            style={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              fontFamily: "ui-monospace, monospace",
              fontSize: 10.5, letterSpacing: "0.20em",
              color: T.brass, fontWeight: 600, textTransform: "uppercase",
              borderBottom: `1px solid ${T.line}`,
              paddingBottom: 12,
            }}
          >
            <span>✦ Through your Concierge®</span>
            <span>Concierge® · Est 1998</span>
          </div>

          <h2
            className="t-serif"
            data-testid="fit-drawer-title"
            style={{
              fontSize: 30, lineHeight: 1.1, fontWeight: 500,
              margin: 0, color: T.ink,
            }}
          >
            {data.name || "—"}
          </h2>

          <div
            data-testid="fit-drawer-status"
            style={{
              fontSize: 13, color: T.ink2,
              display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap",
            }}
          >
            {data.primary_type_label && (
              <span
                style={{
                  fontFamily: "ui-monospace, monospace",
                  fontSize: 10.5, letterSpacing: "0.16em",
                  color: T.brass, fontWeight: 600, textTransform: "uppercase",
                  border: `1px solid ${T.brass}55`,
                  padding: "2px 9px", borderRadius: 999,
                }}
              >{data.primary_type_label}</span>
            )}
            {data.short_address && <span>{data.short_address}</span>}
            {rating && (
              <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                <span style={{ color: T.brass }}>★</span>
                <strong style={{ color: T.ink }}>{rating.toFixed(1)}</strong>
                {ratingCount ? <span style={{ color: T.ink3 }}>({ratingCount.toLocaleString()} reviews)</span> : null}
              </span>
            )}
            {openNow != null && (
              <span
                style={{
                  display: "inline-flex", alignItems: "center", gap: 6,
                  background: openNow ? "#e6f3e9" : "#fbe2e2",
                  color: openNow ? "#1e6b32" : "#922222",
                  padding: "2px 10px", borderRadius: 999,
                  fontSize: 11, fontWeight: 600,
                }}
              >
                <span style={{
                  width: 6, height: 6, borderRadius: 999,
                  background: openNow ? "#2f8a52" : "#c14545",
                }} />
                {openNow ? "Open now" : "Closed"}
              </span>
            )}
            {priceTier && (
              <span
                style={{
                  fontFamily: "ui-monospace, monospace",
                  fontSize: 11, fontWeight: 600, color: T.brass,
                  letterSpacing: "0.10em",
                }}
              >{priceTier}</span>
            )}
          </div>

          {data.amenities && data.amenities.length > 0 && (
            <div
              data-testid="fit-drawer-amenities"
              style={{ display: "flex", flexWrap: "wrap", gap: 6 }}
            >
              {data.amenities.map((a) => (
                <span
                  key={a.code}
                  data-testid={`fit-drawer-amenity-${a.code}`}
                  style={{
                    display: "inline-flex", alignItems: "center", gap: 5,
                    background: `${T.brass}14`, color: T.ink,
                    border: `1px solid ${T.brass}33`,
                    borderRadius: 999,
                    padding: "5px 11px",
                    fontSize: 11.5, fontWeight: 500,
                  }}
                >
                  <span aria-hidden="true">{a.icon}</span>
                  {a.label}
                </span>
              ))}
            </div>
          )}

          {data.editorial_summary && (
            <p
              data-testid="fit-drawer-summary"
              style={{ fontSize: 14.5, lineHeight: 1.55, color: T.ink2, margin: 0 }}
            >
              {data.editorial_summary}
            </p>
          )}

          <DrawerVoiceLine text={voiceLine} T={T} testid="fit-drawer-concierge-voice" />

          <StaticMapBand lat={data.lat} lng={data.lng} label="On the map" testid="fit-drawer-map" />

          {photos.length > 1 && (
            <div
              data-testid="fit-drawer-gallery"
              style={{
                display: "grid",
                gridTemplateColumns: `repeat(${Math.min(photos.length, 4)}, 1fr)`,
                gap: 6,
              }}
            >
              {photos.slice(0, 4).map((p, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => setActivePhoto(i)}
                  data-testid={`fit-drawer-gallery-${i}`}
                  style={{
                    aspectRatio: "4 / 3",
                    background: `url(${p}) center/cover no-repeat`,
                    border: i === activePhoto ? `2px solid ${T.brass}` : `1px solid ${T.line}`,
                    borderRadius: 8, cursor: "pointer", padding: 0,
                  }}
                  aria-label={`Photo ${i + 1}`}
                />
              ))}
            </div>
          )}

          {data.nearby_landmarks && data.nearby_landmarks.length > 0 && (
            <div data-testid="fit-drawer-landmarks" style={{ fontSize: 12.5, color: T.ink2 }}>
              <span
                style={{
                  fontFamily: "ui-monospace, monospace",
                  fontSize: 10, color: T.ink3,
                  letterSpacing: "0.16em", textTransform: "uppercase",
                  fontWeight: 600, marginRight: 8,
                }}
              >Nearby</span>
              {data.nearby_landmarks.map((l, i) => (
                <span key={i} style={{ marginRight: 12 }}>
                  <strong style={{ color: T.ink }}>{l.name}</strong>
                  {l.distance_m ? <span style={{ color: T.ink3 }}> · {Math.round(l.distance_m)}m</span> : null}
                </span>
              ))}
            </div>
          )}

          {/* What's inside · Google's sub-destinations (spa inside hotel etc.) */}
          {data.sub_destinations && data.sub_destinations.length > 0 && (
            <div data-testid="fit-drawer-subdest" style={{ marginTop: 4 }}>
              <h3
                style={{
                  fontFamily: "ui-monospace, monospace",
                  fontSize: 11, color: T.brass,
                  letterSpacing: "0.18em", textTransform: "uppercase",
                  fontWeight: 700, margin: "0 0 8px",
                }}
              >What's inside</h3>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {data.sub_destinations.map((s) => (
                  <span
                    key={s.place_id}
                    data-testid="fit-drawer-subdest-item"
                    style={{
                      background: T.bg,
                      border: `1px solid ${T.line2}`,
                      borderRadius: 999,
                      padding: "5px 11px",
                      fontSize: 12, color: T.ink,
                      fontWeight: 500,
                    }}
                  >{s.name}</span>
                ))}
              </div>
            </div>
          )}

          {/* Local time + plus code · one-line line for the concierge */}
          {(data.time_zone || data.plus_code || data.neighborhood) && (
            <div
              data-testid="fit-drawer-context"
              style={{
                display: "flex", flexWrap: "wrap", gap: 14,
                fontSize: 11.5, color: T.ink3,
                paddingTop: 4,
              }}
            >
              {data.neighborhood && (
                <span>📍 <strong style={{ color: T.ink2 }}>{data.neighborhood}</strong></span>
              )}
              {data.time_zone && (
                <span>🕐 <strong style={{ color: T.ink2 }}>{data.time_zone.replace("_", " ")}</strong>{data.utc_offset_min != null ? <> · UTC{data.utc_offset_min >= 0 ? "+" : ""}{Math.round(data.utc_offset_min / 60)}</> : null}</span>
              )}
              {data.plus_code && (
                <span style={{ fontFamily: "ui-monospace, monospace", fontSize: 10.5 }}>
                  · <strong style={{ color: T.ink2 }}>{data.plus_code}</strong>
                </span>
              )}
            </div>
          )}

          {/* Secondary hours · kitchen / brunch / etc. */}
          {data.secondary_hours && data.secondary_hours.length > 0 && (
            <div data-testid="fit-drawer-secondary-hours" style={{ fontSize: 12, color: T.ink2 }}>
              {data.secondary_hours.map((sh, i) => (
                <details key={i} style={{ marginBottom: 6 }}>
                  <summary style={{
                    cursor: "pointer", fontWeight: 600, color: T.ink,
                    fontFamily: "ui-monospace, monospace",
                    fontSize: 11, letterSpacing: "0.14em",
                    textTransform: "uppercase",
                  }}>
                    {sh.type.replace(/_/g, " ").toLowerCase()} hours
                  </summary>
                  <div style={{ marginTop: 6, paddingLeft: 4, whiteSpace: "pre-line" }}>
                    {sh.weekday_hours.slice(0, 7).join("\n")}
                  </div>
                </details>
              ))}
            </div>
          )}

          {/* Mini-map · removed 30 Jun 2026 (founder ruling).
              The Static Map band at the top of the drawer (G3 · shared
              `<StaticMapBand/>`) replaces this redundant golden tile.
              The "Directions" deeplink below still opens Google Maps. */}

          {(data.directions_url || data.photos_url || data.reviews_url) && (
            <div data-testid="fit-drawer-deeplinks" style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {data.directions_url && (
                <a
                  href={data.directions_url}
                  target="_blank"
                  rel="noreferrer noopener"
                  data-testid="fit-drawer-directions"
                  style={smallChipStyle}
                >Directions ↗</a>
              )}
              {data.photos_url && (
                <a
                  href={data.photos_url}
                  target="_blank"
                  rel="noreferrer noopener"
                  data-testid="fit-drawer-photos-link"
                  style={smallChipStyle}
                >More photos ↗</a>
              )}
              {data.reviews_url && (
                <a
                  href={data.reviews_url}
                  target="_blank"
                  rel="noreferrer noopener"
                  data-testid="fit-drawer-reviews-link"
                  style={smallChipStyle}
                >All reviews ↗</a>
              )}
            </div>
          )}

          <div data-testid="fit-drawer-meta" style={{ display: "grid", gap: 6, fontSize: 13 }}>
            {data.address_line && (
              <DrawerMetaRow label="Address" value={data.address_line} />
            )}
            {data.phone && (
              <DrawerMetaRow
                label="Phone"
                value={<a href={`tel:${data.phone}`} style={{ color: T.ink, textDecoration: "underline" }}>{data.phone}</a>}
              />
            )}
            {data.website && (
              <DrawerMetaRow
                label="Website"
                value={
                  <a href={data.website} target="_blank" rel="noreferrer noopener" style={{ color: T.brass, textDecoration: "underline" }}>
                    {(data.website || "").replace(/^https?:\/\//, "").replace(/\/.*$/, "")}
                  </a>
                }
              />
            )}
            {data.weekday_hours && data.weekday_hours.length > 0 && (
              <DrawerMetaRow
                label="Hours"
                value={
                  <span style={{ whiteSpace: "pre-line", color: T.ink2 }}>
                    {data.weekday_hours.slice(0, 7).join("\n")}
                  </span>
                }
              />
            )}
          </div>

          {loadingDetail && (
            <p style={{ fontSize: 11.5, color: T.ink3, fontStyle: "italic", margin: 0 }}>
              Loading the full atmosphere…
            </p>
          )}

          {/* Reviews · top 3 real Google reviews · shared
             <DrawerReviewsBand> (Parity Sprint Phase 2). */}
          <DrawerReviewsBand reviews={data.reviews} limit={3} T={T} testid="fit-drawer-reviews" />

          <div data-testid="fit-drawer-ctas" style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 6 }}>
            {/* Concierge® first · brand promise · founder ruling 30 Jun 2026 */}
            <button
              type="button"
              onClick={onRequest}
              data-testid="fit-drawer-request"
              style={{
                background: T.ink, color: T.card,
                border: 0, borderRadius: 999,
                padding: "14px 22px",
                fontSize: 14, fontWeight: 600,
                cursor: "pointer", fontFamily: "inherit",
                textAlign: "center",
              }}
            >
              Request via Concierge®
            </button>
            {data.google_maps_url && (
              <a
                href={data.google_maps_url}
                target="_blank"
                rel="noreferrer noopener"
                data-testid="fit-drawer-google"
                style={{
                  background: "transparent", color: T.brass,
                  border: `1.5px solid ${T.brass}`, borderRadius: 999,
                  padding: "12px 22px",
                  fontSize: 13.5, fontWeight: 500,
                  textAlign: "center", textDecoration: "none",
                  fontFamily: "inherit", display: "block",
                  transition: "all 0.15s",
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = T.brass; e.currentTarget.style.color = T.bg; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = T.brass; }}
              >
                Reserve ↗
              </a>
            )}
          </div>

          <p
            style={{
              fontSize: 11.5, color: T.ink3, fontStyle: "italic",
              textAlign: "center", margin: 0, lineHeight: 1.5,
            }}
          >
            Ask your Concierge® team to arrange it · we hold the door open.
          </p>
        </div>
      </aside>
    </div>
  );
}

function DrawerMetaRow({ label, value }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "100px 1fr", gap: 12, alignItems: "start", paddingTop: 4 }}>
      <span style={{
        fontFamily: "ui-monospace, monospace",
        fontSize: 10, color: T.ink3,
        letterSpacing: "0.16em", textTransform: "uppercase",
        fontWeight: 600,
      }}>{label}</span>
      <span>{value}</span>
    </div>
  );
}

const chromeBtnStyle = {
  background: "transparent",
  border: `1px solid ${T.line2}`,
  borderRadius: 999,
  padding: "5px 12px",
  fontSize: 10.5, letterSpacing: "0.16em",
  color: T.ink2, cursor: "pointer",
  fontFamily: "ui-monospace, monospace",
  fontWeight: 600, textTransform: "uppercase",
};

const smallChipStyle = {
  background: "transparent",
  border: `1px solid ${T.line2}`,
  borderRadius: 999,
  padding: "5px 11px",
  fontSize: 11.5, color: T.ink2,
  textDecoration: "none",
  fontFamily: "inherit", fontWeight: 500,
};


/* ── Smart filter bar + filtered/sorted grid · same as /stay but no
 *    "Type" filter (every category chip already biases the type). */

const _PRICE_TIER_BY_LEVEL = {
  PRICE_LEVEL_INEXPENSIVE:    "$",
  PRICE_LEVEL_MODERATE:       "$$",
  PRICE_LEVEL_EXPENSIVE:      "$$$",
  PRICE_LEVEL_VERY_EXPENSIVE: "$$$$",
};

function FitFilterBar({
  cards, filterPrice, setFilterPrice,
  filterMinRating, setFilterMinRating,
  sortBy, setSortBy,
}) {
  const availablePrices = useMemo(() => {
    const s = new Set();
    cards.forEach((c) => { if (c.price_level && _PRICE_TIER_BY_LEVEL[c.price_level]) s.add(_PRICE_TIER_BY_LEVEL[c.price_level]); });
    return ["$", "$$", "$$$", "$$$$"].filter((p) => s.has(p));
  }, [cards]);

  return (
    <div
      data-testid="fit-filter-bar"
      style={{
        background: T.card,
        border: `1px solid ${T.line}`,
        borderRadius: 14,
        padding: "12px 16px",
        margin: "8px 0 22px",
        display: "flex", flexDirection: "column", gap: 10,
        boxShadow: "0 2px 8px rgba(33,28,21,0.03)",
      }}
    >
      {availablePrices.length > 0 && (
        <FilterRow label="Price" testid="fit-filter-price">
          <Chip on={filterPrice === "any"} onClick={() => setFilterPrice("any")} testid="fit-filter-price-any">Any</Chip>
          {availablePrices.map((p) => (
            <Chip key={p} on={filterPrice === p} onClick={() => setFilterPrice(p)} testid={`fit-filter-price-${p}`}>{p}</Chip>
          ))}
        </FilterRow>
      )}
      <FilterRow label="Rating" testid="fit-filter-rating">
        <Chip on={filterMinRating === 0}   onClick={() => setFilterMinRating(0)}   testid="fit-filter-rating-any">Any</Chip>
        <Chip on={filterMinRating === 3}   onClick={() => setFilterMinRating(3)}   testid="fit-filter-rating-3">★3+</Chip>
        <Chip on={filterMinRating === 4}   onClick={() => setFilterMinRating(4)}   testid="fit-filter-rating-4">★4+</Chip>
        <Chip on={filterMinRating === 4.5} onClick={() => setFilterMinRating(4.5)} testid="fit-filter-rating-45">★4.5+</Chip>
      </FilterRow>
      <FilterRow label="Sort by" testid="fit-filter-sort">
        <Chip on={sortBy === "rating"}     onClick={() => setSortBy("rating")}     testid="fit-sort-rating">Top rated</Chip>
        <Chip on={sortBy === "popularity"} onClick={() => setSortBy("popularity")} testid="fit-sort-pop">Most reviewed</Chip>
        <Chip on={sortBy === "price_asc"}  onClick={() => setSortBy("price_asc")}  testid="fit-sort-pasc">Price ↑</Chip>
        <Chip on={sortBy === "price_desc"} onClick={() => setSortBy("price_desc")} testid="fit-sort-pdesc">Price ↓</Chip>
        <Chip on={sortBy === "name"}       onClick={() => setSortBy("name")}       testid="fit-sort-name">A → Z</Chip>
      </FilterRow>
    </div>
  );
}

function FilterRow({ label, testid, children }) {
  return (
    <div
      data-testid={testid}
      style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}
    >
      <span
        style={{
          fontFamily: "ui-monospace, monospace",
          fontSize: 10.5, color: T.ink3,
          letterSpacing: "0.16em", textTransform: "uppercase",
          fontWeight: 600, minWidth: 56,
        }}
      >{label}</span>
      {children}
    </div>
  );
}

function Chip({ on, onClick, testid, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      style={{
        background: on ? T.ink : "transparent",
        color: on ? T.bg : T.ink2,
        border: `1px solid ${on ? T.ink : T.line2}`,
        borderRadius: 999,
        padding: "5px 12px",
        fontSize: 12, fontWeight: 500,
        cursor: "pointer", fontFamily: "inherit",
        transition: "all 0.12s",
      }}
    >{children}</button>
  );
}

const _PRICE_RANK = {
  PRICE_LEVEL_INEXPENSIVE:    1,
  PRICE_LEVEL_MODERATE:       2,
  PRICE_LEVEL_EXPENSIVE:      3,
  PRICE_LEVEL_VERY_EXPENSIVE: 4,
};

function FilteredResultsGrid({ cards, filterPrice, filterMinRating, sortBy, onOpen, onBrief }) {
  const list = useMemo(() => {
    let out = (cards || []).slice();
    if (filterPrice !== "any") {
      out = out.filter((c) => _PRICE_TIER_BY_LEVEL[c.price_level] === filterPrice);
    }
    if (filterMinRating > 0) {
      out = out.filter((c) => (c.rating || 0) >= filterMinRating);
    }
    out.sort((a, b) => {
      if (sortBy === "rating") {
        return (b.rating || 0) - (a.rating || 0);
      }
      if (sortBy === "popularity") {
        return (b.user_ratings_total || 0) - (a.user_ratings_total || 0);
      }
      if (sortBy === "price_asc") {
        return (_PRICE_RANK[a.price_level] || 99) - (_PRICE_RANK[b.price_level] || 99);
      }
      if (sortBy === "price_desc") {
        return (_PRICE_RANK[b.price_level] || 0) - (_PRICE_RANK[a.price_level] || 0);
      }
      if (sortBy === "name") {
        return (a.name || "").localeCompare(b.name || "");
      }
      return 0;
    });
    return out;
  }, [cards, filterPrice, filterMinRating, sortBy]);

  if (list.length === 0) {
    return (
      <div
        data-testid="fit-filtered-empty"
        style={{
          padding: 36, textAlign: "center", color: T.ink3,
          fontSize: 13.5, fontStyle: "italic",
          border: `1px dashed ${T.line2}`, borderRadius: 14,
        }}
      >
        No venues match those filters. Loosen them above.
      </div>
    );
  }

  return (
    <div
      data-testid="fit-results"
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
        gap: 18,
      }}
    >
      {list.map((h, i) => (
        <PillarCard
          key={h.place_id || i}
          venue={h}
          testidPrefix="fit"
          onView={() => onOpen(h)}
          onBrief={() => onBrief?.(h)}
          briefLabel="Send a brief"
          topLeftPill={h.primary_type_label ? (
            <span style={{
              background: "rgba(252,250,244,0.92)", color: T.brass,
              padding: "3px 9px", borderRadius: 999,
              fontFamily: "ui-monospace, monospace",
              fontSize: 10, letterSpacing: "0.14em",
              fontWeight: 600, textTransform: "uppercase",
              border: `1px solid ${T.brass}44`,
            }}>{h.primary_type_label}</span>
          ) : null}
          topRightPill={_priceTierLabel(h.price_level) ? (
            <span data-testid="fit-card-price-tier" style={{
              background: "rgba(33,28,21,0.78)", color: T.bg,
              padding: "3px 9px", borderRadius: 999,
              fontFamily: "ui-monospace, monospace",
              fontSize: 10, letterSpacing: "0.16em", fontWeight: 600,
            }}>{_priceTierLabel(h.price_level)}</span>
          ) : null}
        />
      ))}
    </div>
  );
}
